import { ChangeDetectionStrategy, Component, computed, effect, input, signal } from '@angular/core';
import type { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { ReactiveFormsModule, Validators } from '@angular/forms';

import {
  FormFieldComponent,
  InputComponent,
  LabelComponent,
  SelectComponent,
} from '@core/user-interface-legacy';

import { Destroyable, takeUntilDestroyed } from '../../../common';
import {
  COMPENSATION_PROFILE_TRAINING_STUDY_ID,
  COMPENSATION_PROFILE_NO_SALARY_ID,
  COMPENSATION_PROFILE_COMMON_OPTIONS,
  COMPENSATION_PROFILE_APPRENTICESHIP_OPTIONS,
  COMPENSATION_TYPE_FIXED_ID,
  COMPENSATION_TYPE_RANGE_ID,
  COMPENSATION_TYPE_OPTIONS,
  OFFER_TYPE_WORKPLACE_ID,
  OFFER_TYPE_SELF_EMPLOYMENT_ID,
  OFFER_TYPE_APPRENTICESHIP_ID,
  OFFER_TYPE_INTERNSHIP_ID,
} from '../constants';

const COMPENSATION_TYPE_REQUIRED_FOR_OFFER_TYPES = [
  OFFER_TYPE_WORKPLACE_ID,
  OFFER_TYPE_SELF_EMPLOYMENT_ID,
  OFFER_TYPE_INTERNSHIP_ID,
];

@Component({
  selector: 'rti-gfea-salary-input',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './gfea-salary-input.component.html',
  styleUrls: ['./gfea-salary-input.component.less'],
  standalone: true,
  imports: [
    FormFieldComponent,
    InputComponent,
    LabelComponent,
    ReactiveFormsModule,
    SelectComponent,
  ],
})
@Destroyable()
export class GfeaSalaryInputComponent {
  public group = input.required<UntypedFormGroup>();
  public offerType = input.required<string>();
  public isInEditMode = input.required<boolean>();

  public COMPENSATION_PROFILE_COMMON_OPTIONS = COMPENSATION_PROFILE_COMMON_OPTIONS;
  public COMPENSATION_PROFILE_APPRENTICESHIP_OPTIONS = COMPENSATION_PROFILE_APPRENTICESHIP_OPTIONS;
  public COMPENSATION_TYPE_OPTIONS = COMPENSATION_TYPE_OPTIONS;

  public salaryRangeError = computed(() => {
    if (!this.group() || !this.salaryRangeFrom() || !this.salaryRangeTo()) {
      return false;
    }

    return !!(
      this.salaryRangeFrom() &&
      this.salaryRangeTo() &&
      Number(this.salaryRangeFrom()!) >= Number(this.salaryRangeTo()!)
    );
  });
  public compensationProfileOptions = computed(() => {
    if (this.offerType() === OFFER_TYPE_APPRENTICESHIP_ID) {
      return COMPENSATION_PROFILE_APPRENTICESHIP_OPTIONS;
    } else {
      return COMPENSATION_PROFILE_COMMON_OPTIONS;
    }
  });
  public compensationTypeRequired = computed(
    () =>
      !!this.offerType() &&
      !!this.compensationProfile() &&
      COMPENSATION_TYPE_REQUIRED_FOR_OFFER_TYPES.includes(this.offerType()) &&
      this.compensationProfile() !== COMPENSATION_PROFILE_NO_SALARY_ID,
  );
  public fixedSalaryRequired = computed(
    () => this.compensationTypeRequired() && this.compensationType() === COMPENSATION_TYPE_FIXED_ID,
  );
  public salaryRangeRequired = computed(
    () => this.compensationTypeRequired() && this.compensationType() === COMPENSATION_TYPE_RANGE_ID,
  );
  public apprenticePayRequired = computed(
    () => this.compensationProfile() === COMPENSATION_PROFILE_TRAINING_STUDY_ID,
  );

  private compensationProfile = signal<string | null>(null);
  private compensationType = signal<string | null>(null);
  private salaryRangeFrom = signal<number | null>(null);
  private salaryRangeTo = signal<number | null>(null);
  private oldOfferType: string | null = null;

  constructor() {
    effect(() => {
      if (this.isInEditMode() && !this.oldOfferType) {
        this.initEditMode();
        this.oldOfferType = this.offerType();
      } else if (this.oldOfferType !== this.offerType()) {
        this.resetFieldValue('compensationProfile');
        this.oldOfferType = this.offerType();
      }
    });
  }

  public ngOnInit(): void {
    this.group()
      .get('compensationProfile')
      ?.valueChanges.pipe(takeUntilDestroyed(this))
      .subscribe(value => {
        this.compensationProfile.set(value);
        this.maybeActivateField('compensationType', this.compensationTypeRequired());
        this.maybeActivateField('fixedSalary', this.fixedSalaryRequired());
        this.maybeActivateField('salaryRangeFrom', this.salaryRangeRequired());
        this.maybeActivateField('salaryRangeTo', this.salaryRangeRequired());
        this.maybeActivateField('apprenticePay', this.apprenticePayRequired());
        this.maybeActivateField('year1', this.apprenticePayRequired());
      });

    this.group()
      .get('compensationType')
      ?.valueChanges.pipe(takeUntilDestroyed(this))
      .subscribe(value => {
        this.compensationType.set(value);
        this.maybeActivateField('fixedSalary', this.fixedSalaryRequired());
        this.maybeActivateField('salaryRangeFrom', this.salaryRangeRequired());
        this.maybeActivateField('salaryRangeTo', this.salaryRangeRequired());
        this.maybeActivateField('apprenticePay', this.apprenticePayRequired());
        this.maybeActivateField('year1', this.apprenticePayRequired());
      });

    this.group()
      .get('salaryRangeFrom')
      ?.valueChanges.pipe(takeUntilDestroyed(this))
      .subscribe(value => {
        this.salaryRangeFrom.set(value);
      });

    this.group()
      .get('salaryRangeTo')
      ?.valueChanges.pipe(takeUntilDestroyed(this))
      .subscribe(value => {
        this.salaryRangeTo.set(value);
      });
  }

  private initEditMode(): void {
    this.compensationProfile.set(this.group().get('compensationProfile')?.value);
    this.compensationType.set(this.group().get('compensationType')?.value);
    this.salaryRangeFrom.set(this.group().get('salaryRangeFrom')?.value);
    this.salaryRangeTo.set(this.group().get('salaryRangeTo')?.value);

    this.maybeActivateField('compensationType', this.compensationTypeRequired());
    this.maybeActivateField('fixedSalary', this.fixedSalaryRequired());
    this.maybeActivateField('salaryRangeFrom', this.salaryRangeRequired());
    this.maybeActivateField('salaryRangeTo', this.salaryRangeRequired());
    this.maybeActivateField('apprenticePay', this.apprenticePayRequired());
    this.maybeActivateField('year1', this.apprenticePayRequired());
  }

  private maybeActivateField(fieldName: string, condition: boolean): void {
    const control = this.group().get(fieldName)!;
    if (!control) {
      return;
    }

    if (condition && !control.hasValidator(Validators.required)) {
      control.addValidators([Validators.required]);
      control.updateValueAndValidity({ emitEvent: false });
    } else if (!condition && control.hasValidator(Validators.required)) {
      control.removeValidators([Validators.required]);
      this.resetFieldValue(fieldName, control);
    }
  }

  private resetFieldValue(fieldName: string, control: AbstractControl | null = null): void {
    const formControl = control || this.group().get(fieldName);
    if (!formControl) {
      return;
    }

    formControl.reset();
    formControl.updateValueAndValidity({ emitEvent: false });
  }
}
